import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const API = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// ---------------------------------------------------------------------------
// Silent session refresh (2026-07)
// Access tokens expire after 15 minutes. On any 401 from an authenticated
// route, call /auth/refresh ONCE (single-flight across concurrent 401s),
// then retry the original request. If refresh itself fails, broadcast
// "webjesters:auth-expired" so AuthContext logs the user out cleanly and
// ProtectedRoute redirects to /login.
// ---------------------------------------------------------------------------
const NO_REFRESH_PATHS = ["/auth/login", "/auth/refresh", "/auth/logout"];
let refreshPromise = null;
function refreshAccessToken() {
    if (!refreshPromise) {
        refreshPromise = api
            .post("/auth/refresh")
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
}
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config;
        const status = error.response?.status;
        const url = original?.url || "";
        const skip =
            status !== 401 ||
            !original ||
            original._retried ||
            NO_REFRESH_PATHS.some((p) => url.includes(p));
        if (skip) return Promise.reject(error);
        original._retried = true;
        try {
            await refreshAccessToken();
            return api(original);
        } catch (_refreshErr) {
            // Refresh cookie is gone/expired — session is truly over.
            window.dispatchEvent(new Event("webjesters:auth-expired"));
            return Promise.reject(error);
        }
    }
);

/**
 * Public-flow axios instance — for endpoints that need NEITHER to send
 * nor receive cookies. As of 2026-06 the Emergent edge proxy rewrites the
 * `Access-Control-Allow-Origin` header to `*` on non-2xx responses.
 * Combined with `Access-Control-Allow-Credentials: true`, that is a
 * forbidden CORS combo and browsers reject the entire response — which is
 * why customers got "Something went wrong" on password-reset / login error
 * paths. The fix is to call these endpoints with `withCredentials: false`,
 * because browsers DON'T enforce the wildcard rule when credentials aren't
 * requested. This is purely a frontend-side routing fix; the backend
 * response is unchanged.
 *
 * Use this for: password-reset/request, password-reset/confirm, and any
 * other endpoint that is fully token-or-payload authenticated (no cookies).
 * DO NOT use it for: /auth/login, /auth/me, /auth/refresh, /auth/logout,
 * or any authenticated route — those need cookies.
 */
export const publicApi = axios.create({
    baseURL: API,
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
});

/**
 * Format an axios error's `detail` into a human-friendly string.
 *
 * Real-world failure modes that produce no JSON `detail`:
 *  - 5xx / 520 / Cloudflare HTML page  → response.data is HTML, no .detail
 *  - CORS rejection (ACAO mismatch)    → response is undefined
 *  - Network down / DNS / TLS error    → response is undefined
 *
 * The default "Something went wrong" copy is too generic and has caused
 * real customer support tickets (e.g. mistyped password landed on a CORS
 * wildcard 401, which axios surfaced as a no-response error). Give people
 * an answer they can ACT on.
 */
export function formatApiErrorDetail(detail) {
    if (detail == null) return "Couldn't reach the server. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

/**
 * Build a friendly login/auth error message from an axios error object.
 * Handles three failure shapes:
 *   1. Backend returned a JSON {detail: "..."} → use it
 *   2. Backend returned non-JSON (HTML 502/520, Cloudflare challenge) → say so
 *   3. Network error / CORS blocked entirely (e.response undefined) → say so
 */
export function formatAuthError(err) {
    if (!err) return "Couldn't reach the server. Please try again.";
    // Case 3: no response at all (network down, DNS, TLS, CORS preflight reject)
    if (!err.response) {
        if (err.code === "ERR_NETWORK") {
            return "We can't reach WebJesters right now. Check your connection and try again.";
        }
        return "Couldn't reach the server. Please try again in a moment.";
    }
    const status = err.response.status;
    const data = err.response.data;
    // Case 1: well-formed JSON detail
    if (data && typeof data === "object" && data.detail) {
        return formatApiErrorDetail(data.detail);
    }
    // Case 2: HTML / non-JSON body (Cloudflare 520 page, gateway timeout, etc.)
    if (status >= 500) {
        return "The portal is temporarily unavailable. Please try again in a minute.";
    }
    if (status === 401 || status === 403) {
        return "Invalid email or password.";
    }
    if (status === 429) {
        return "Too many attempts. Please wait a few minutes and try again.";
    }
    return `Request failed (${status}). Please try again.`;
}

export default api;
